import { useState, useEffect } from 'react'
import api from '../../lib/api'
import { useBrand } from '@/hooks/useBrand'
import {
  Search,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  Phone,
  Users,
  CheckCircle2,
  Eye,
  MessageSquare,
  Settings,
  PhoneCall,
  Activity,
  AlertTriangle
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/dashboard-ui'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'

interface DemoLead {
  id: string
  contact_name: string
  phone_number: string
  website_url: string
  firm_name: string | null
  practice_areas: string[] | null
  case_type: string | null
  website_description: string | null
  scrape_status: string
  call_status: string
  call_duration_seconds: number | null
  transcript_text: string | null
  call_summary: string | null
  follow_up_status: string
  follow_up_notes: string | null
  utm_source: string | null
  created_at: string
}

interface Stats {
  total: string
  completed: string
  failed: string
  new_leads: string
  converted: string
  today: string
}

const CALL_STATUS_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  pending: { variant: 'outline', label: 'Pending' },
  calling: { variant: 'secondary', label: 'Calling' },
  initiated: { variant: 'secondary', label: 'Initiated' },
  in_progress: { variant: 'default', label: 'In Progress' },
  completed: { variant: 'default', label: 'Completed' },
  failed: { variant: 'destructive', label: 'Failed' },
}

const FOLLOW_UP_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  new: { variant: 'outline', label: 'New' },
  contacted: { variant: 'secondary', label: 'Contacted' },
  qualified: { variant: 'default', label: 'Qualified' },
  converted: { variant: 'default', label: 'Converted' },
  dismissed: { variant: 'destructive', label: 'Dismissed' },
}

function DemoConfigCard() {
  const [config, setConfig] = useState<any>(null)
  const [testPhone, setTestPhone] = useState('')
  const [testWebsite, setTestWebsite] = useState('')
  const [isCalling, setIsCalling] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [showConfig, setShowConfig] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    api.get('/api/platform/demo-config').then(res => {
      if (res.data.success) setConfig(res.data.config)
    }).catch(() => {})
  }, [])

  const handleTestCall = async () => {
    if (!testPhone.trim()) {
      toast({ variant: 'destructive', title: 'Enter a phone number' })
      return
    }
    setIsCalling(true)
    try {
      await api.post('/api/platform/demo-config/test-call', {
        phoneNumber: testPhone,
        websiteUrl: testWebsite || 'https://www.revilllawfirm.com',
        name: 'Admin Test',
      })
      toast({ title: 'Test call initiated — check your phone!' })
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Test call failed', description: err.response?.data?.message || err.message })
    } finally {
      setIsCalling(false)
    }
  }

  const handleSyncEnglishPro = async () => {
    setIsSyncing(true)
    try {
      const res = await api.post('/api/platform/demo-config/sync-english-pro')
      if (res.data?.config) setConfig(res.data.config)
      toast({ title: 'Demo agent synced to English Pro' })
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Sync failed', description: err.response?.data?.message || err.response?.data?.error || err.message })
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <Card className="card-inset">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Demo Agent Config</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowConfig(!showConfig)}>
            {showConfig ? 'Hide' : 'Show'}
          </Button>
        </div>
      </CardHeader>
      {showConfig && config && (
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Provider</p>
              <Badge variant={config.provider === 'telnyx' ? 'default' : 'secondary'}>{config.provider}</Badge>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Phone</p>
              <p className="font-medium">{config.phoneDisplay}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Daily Cap</p>
              <p className="font-medium">{config.todayCount} / {config.dailyCap}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Agent Status</p>
              <div className="flex items-center gap-1">
                <Activity className={`h-3 w-3 ${config.agentStatus === 'active' ? 'text-green-500' : 'text-red-500'}`} />
                <span className={config.agentStatus === 'active' ? 'text-green-600' : 'text-red-600'}>{config.agentStatus}</span>
              </div>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">English Pro</p>
              {config.englishPro ? (
                <Badge variant={config.englishPro.matches ? 'default' : 'destructive'}>
                  {config.englishPro.matches ? 'Matched' : `${config.englishPro.drift?.length || 0} drift`}
                </Badge>
              ) : (
                <Badge variant="outline">Unknown</Badge>
              )}
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Agent ID: <code className="bg-muted px-1 rounded">{config.agentId}</code>
          </div>

          {config.englishPro && !config.englishPro.matches && (
            <div className="rounded-md border border-amber-300/70 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">Demo agent does not match English Pro</p>
                    <p className="mt-1 text-amber-800/80 dark:text-amber-100/75">
                      Drift: {config.englishPro.drift.slice(0, 4).map((d: any) => d.field).join(', ')}
                      {config.englishPro.drift.length > 4 ? `, +${config.englishPro.drift.length - 4} more` : ''}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleSyncEnglishPro} disabled={isSyncing}>
                  <RefreshCw className={`mr-2 h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing...' : 'Sync'}
                </Button>
              </div>
            </div>
          )}

          {/* Test Call */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-2">Send Test Call</p>
            <div className="flex gap-2">
              <Input
                placeholder="Phone number"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                className="max-w-[200px]"
              />
              <Input
                placeholder="Website URL (optional)"
                value={testWebsite}
                onChange={e => setTestWebsite(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleTestCall} disabled={isCalling} size="sm">
                <PhoneCall className={`mr-2 h-3 w-3 ${isCalling ? 'animate-pulse' : ''}`} />
                {isCalling ? 'Calling...' : 'Call'}
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

export default function DemoLeadsPage() {
  const brand = useBrand()
  const [leads, setLeads] = useState<DemoLead[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [followUpFilter, setFollowUpFilter] = useState('all')
  const [callStatusFilter, setCallStatusFilter] = useState('all')
  const [offset, setOffset] = useState(0)
  const [selectedLead, setSelectedLead] = useState<DemoLead | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const limit = 25
  const { toast } = useToast()

  const loadLeads = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (followUpFilter !== 'all') params.set('followUpStatus', followUpFilter)
      if (callStatusFilter !== 'all') params.set('callStatus', callStatusFilter)
      params.set('limit', String(limit))
      params.set('offset', String(offset))

      const res = await api.get(`/api/platform/demo-leads?${params}`)
      if (res.data.success) {
        setLeads(res.data.leads)
        setStats(res.data.stats)
        setTotal(res.data.total)
      }
    } catch {
      toast({ variant: 'destructive', title: 'Failed to load demo leads' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { loadLeads() }, [search, followUpFilter, callStatusFilter, offset])

  const handleUpdateStatus = async (id: string, followUpStatus: string) => {
    try {
      await api.put(`/api/platform/demo-leads/${id}`, { followUpStatus })
      toast({ title: `Status updated to ${followUpStatus}` })
      loadLeads()
    } catch {
      toast({ variant: 'destructive', title: 'Failed to update status' })
    }
  }

  const handleReset = async (id: string) => {
    try {
      await api.post(`/api/platform/demo-leads/${id}/reset`)
      toast({ title: 'Rate limit reset — they can test again' })
      loadLeads()
    } catch {
      toast({ variant: 'destructive', title: 'Failed to reset' })
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/api/platform/demo-leads/${id}`)
      toast({ title: 'Demo lead deleted' })
      if (selectedLead?.id === id) { setShowDetail(false); setSelectedLead(null) }
      loadLeads()
    } catch {
      toast({ variant: 'destructive', title: 'Failed to delete' })
    }
  }

  const formatDate = (d: string) => {
    const date = new Date(d)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }

  const formatDomain = (url: string) => {
    try {
      return new URL(url).hostname.replace('www.', '')
    } catch {
      return url
    }
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Demo Leads"
        subtitle={`Prospects who tried the "Try ${brand.appName}" demo.`}
      />

      {/* Demo Agent Config */}
      <DemoConfigCard />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="card-inset">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold">{stats.total}</p></CardContent>
          </Card>
          <Card className="card-inset">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Today</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold">{stats.today}</p></CardContent>
          </Card>
          <Card className="card-inset">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold text-green-500">{stats.completed}</p></CardContent>
          </Card>
          <Card className="card-inset">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold text-red-500">{stats.failed}</p></CardContent>
          </Card>
          <Card className="card-inset">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">New Leads</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold text-blue-500">{stats.new_leads}</p></CardContent>
          </Card>
          <Card className="card-inset">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Converted</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold text-emerald-500">{stats.converted}</p></CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="card-inset">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, phone, website..."
                value={search}
                onChange={e => { setSearch(e.target.value); setOffset(0) }}
                className="pl-9"
              />
            </div>
            <Select value={followUpFilter} onValueChange={v => { setFollowUpFilter(v); setOffset(0) }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Follow-up" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={callStatusFilter} onValueChange={v => { setCallStatusFilter(v); setOffset(0) }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Call Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Calls</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-inset">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>Firm / Case Type</TableHead>
                <TableHead>Call</TableHead>
                <TableHead>Follow-up</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No demo leads yet</TableCell>
                </TableRow>
              ) : leads.map(lead => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{lead.contact_name}</p>
                      <p className="text-xs text-muted-foreground">{lead.phone_number}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <a href={lead.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:underline">
                      {formatDomain(lead.website_url)}
                    </a>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{lead.firm_name || '—'}</p>
                      {lead.case_type && <p className="text-xs text-muted-foreground">{lead.case_type}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={CALL_STATUS_BADGES[lead.call_status]?.variant || 'outline'}>
                      {CALL_STATUS_BADGES[lead.call_status]?.label || lead.call_status}
                    </Badge>
                    {lead.call_duration_seconds != null && lead.call_duration_seconds > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{Math.round(lead.call_duration_seconds)}s</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={FOLLOW_UP_BADGES[lead.follow_up_status]?.variant || 'outline'}>
                      {FOLLOW_UP_BADGES[lead.follow_up_status]?.label || lead.follow_up_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(lead.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setSelectedLead(lead); setShowDetail(true) }}>
                          <Eye className="mr-2 h-4 w-4" /> View Details
                        </DropdownMenuItem>
                        {lead.transcript_text && (
                          <DropdownMenuItem onClick={() => { setSelectedLead(lead); setShowTranscript(true) }}>
                            <MessageSquare className="mr-2 h-4 w-4" /> View Transcript
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleUpdateStatus(lead.id, 'contacted')}>
                          <Phone className="mr-2 h-4 w-4" /> Mark Contacted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdateStatus(lead.id, 'qualified')}>
                          <CheckCircle2 className="mr-2 h-4 w-4" /> Mark Qualified
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdateStatus(lead.id, 'converted')}>
                          <Users className="mr-2 h-4 w-4" /> Mark Converted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleReset(lead.id)}>
                          <RefreshCw className="mr-2 h-4 w-4" /> Reset (Allow Re-test)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-red-500">
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Demo Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="font-medium">{selectedLead.contact_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedLead.phone_number}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Website</p>
                  <a href={selectedLead.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm break-all">
                    {selectedLead.website_url}
                  </a>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Firm Name</p>
                  <p>{selectedLead.firm_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Case Type</p>
                  <p>{selectedLead.case_type || '—'}</p>
                </div>
              </div>

              {selectedLead.practice_areas && selectedLead.practice_areas.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Practice Areas Detected</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedLead.practice_areas.map((pa, i) => (
                      <Badge key={i} variant="secondary">{pa}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedLead.website_description && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Website Summary</p>
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded p-3">{selectedLead.website_description}</p>
                </div>
              )}

              {selectedLead.call_summary && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Call Summary</p>
                  <p className="text-sm bg-muted/50 rounded p-3">{selectedLead.call_summary}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Call Status</p>
                  <Badge variant={CALL_STATUS_BADGES[selectedLead.call_status]?.variant || 'outline'}>
                    {CALL_STATUS_BADGES[selectedLead.call_status]?.label || selectedLead.call_status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p>{selectedLead.call_duration_seconds ? `${selectedLead.call_duration_seconds}s` : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">UTM Source</p>
                  <p className="text-sm">{selectedLead.utm_source || '—'}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => handleReset(selectedLead.id)}>
                  <RefreshCw className="mr-2 h-3 w-3" /> Allow Re-test
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedLead.id)}>
                  <Trash2 className="mr-2 h-3 w-3" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transcript Dialog */}
      <Dialog open={showTranscript} onOpenChange={setShowTranscript}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Call Transcript — {selectedLead?.contact_name}</DialogTitle>
          </DialogHeader>
          {selectedLead?.transcript_text && (
            <div className="space-y-2">
              {selectedLead.transcript_text.split('\n').map((line, i) => {
                const isAI = line.startsWith('AI:')
                return (
                  <p key={i} className={`text-sm leading-relaxed ${isAI ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                    <span className="font-medium">{line.split(':')[0]}:</span>
                    {line.substring(line.indexOf(':') + 1)}
                  </p>
                )
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
